import React from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { useNotification } from 'segments/hooks/useNotification';

import { InputStr, SubmiteButton } from 'shared/FormItems';
import { ModalFooter } from 'shared/Modals';

import { CoachesSelector } from 'features/coaches';
import { UpdateGroupeParams, useUpdateGroupeMutation } from 'features/groups';
import { SelectUsers } from 'features/users';

interface FormData extends Omit<UpdateGroupeParams, 'userIds'> {
  usersId: number[];
}

interface Props {
  isModalOpen: boolean;
  handleCancel: () => void;
  data?: FormData;
}

const EditModal = ({ isModalOpen, handleCancel, data }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<FormData>();

  const [update, { isSuccess, isError, isLoading, error }] = useUpdateGroupeMutation();

  useNotification({
    isSuccess,
    isError,
    error,
    onSuccess: () => handleCancel(),
    onError: () => handleCancel()
  });

  if (data === undefined) {
    return;
  }

  const onFinish = (values: FormData) => {
    update({
      id: data.id,
      ...(values.coachId && { coachId: values.coachId }),
      ...(values.name && values.name !== data.name && { name: values.name }),
      ...(values.price && { price: values.price }),
      ...(values.priceIndividual && { priceIndividual: values.priceIndividual }),
      userIds: values.usersId
    });
  };

  return (
    <Modal
      title={t('groups.title.edit')}
      open={isModalOpen}
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <ModalFooter
          key="modalFooter"
          handleCancel={handleCancel}
          resetFields={form.resetFields}
        />
      ]}
    >
      {
        data === undefined
          ? <LoadingOutlined />
          : (
            <Form
              name="basic"
              layout="vertical"
              form={form}
              initialValues={{ ...data }}
              onFinish={onFinish}
              autoComplete="off"
              className="edit-user__form"
            >
              <InputStr type='name' formProps={{ rules: [{ required: true }] }} />
              <InputStr type='price' formProps={{ rules: [{ required: true }] }} />
              <InputStr type='priceIndividual' formProps={{ rules: [{ required: true }] }} />
              <CoachesSelector oneSelectMode formProps={{ rules: [{ required: true }] }} />
              <SelectUsers />
              <SubmiteButton submiteText={t('user.edit-data')} isLoading={isLoading} />
            </Form>
          )
      }
    </Modal >
  );
};

export default EditModal;
